// PageViewsPanel — admin-only visitor analytics with daily/monthly/quarterly/annual toggles.
// Mounted beside KBAnalyticsPanel + TesterActivityPanel on the admin dashboard.
import React, { useState, useEffect, useCallback } from 'react';
import { BarChart3, Eye, Users, Loader2, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WINDOWS = [
  { id: 'daily',     label: 'Daily',     hint: 'last 24h' },
  { id: 'monthly',   label: 'Monthly',   hint: 'last 30d' },
  { id: 'quarterly', label: 'Quarterly', hint: 'last 90d' },
  { id: 'annual',    label: 'Annual',    hint: 'last 365d' },
];

export default function PageViewsPanel() {
  const [window, setWindow] = useState('daily');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (w) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(
        `${API_URL}/api/admin/analytics/pageviews?window=${w}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`HTTP ${res.status}: ${text.slice(0, 120)}`);
      }
      setData(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(window); }, [window, fetchData]);

  const totals = data?.totals || { views: 0, unique_visitors: 0 };
  const perPage = data?.per_page || [];
  const trend = data?.trend || [];
  const maxBucketViews = trend.reduce((m, t) => Math.max(m, t.views), 0) || 1;
  const maxPageViews = perPage.reduce((m, p) => Math.max(m, p.views), 0) || 1;

  return (
    <div className="bg-[#111111] border border-[#27272A] rounded-sm p-5" data-testid="pageviews-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-[#FF5E1A]" />
          <h3 className="font-['Outfit'] text-lg font-black text-white">Visitor Analytics</h3>
        </div>
        <div className="flex gap-1 bg-[#0A0A0A] border border-[#27272A] rounded-sm p-0.5">
          {WINDOWS.map((w) => (
            <button
              key={w.id}
              onClick={() => setWindow(w.id)}
              data-testid={`pageviews-window-${w.id}`}
              className={`px-3 py-1 text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider rounded-sm transition-colors ${
                window === w.id
                  ? 'bg-[#FF5E1A] text-white'
                  : 'text-zinc-400 hover:text-white'
              }`}
              title={w.hint}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="p-3 border border-red-500/40 bg-red-500/10 rounded-sm text-xs text-red-300 mb-4">
          {error}
        </div>
      )}

      {/* Headline totals */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
        <StatTile
          icon={<Eye className="w-4 h-4" />}
          label="Total views"
          value={totals.views}
          loading={loading}
          testId="pageviews-total-views"
        />
        <StatTile
          icon={<Users className="w-4 h-4" />}
          label="Unique visitors"
          value={totals.unique_visitors}
          loading={loading}
          testId="pageviews-unique-visitors"
        />
        <StatTile
          icon={<TrendingUp className="w-4 h-4" />}
          label="Pages hit"
          value={perPage.length}
          loading={loading}
          testId="pageviews-pages-count"
        />
      </div>

      {/* Per-page table */}
      <div className="mb-5">
        <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.16em] text-zinc-500 mb-2">
          Top pages
        </div>
        <div className="space-y-1.5 max-h-64 overflow-y-auto">
          {perPage.length === 0 && !loading && (
            <div className="text-xs text-zinc-500 italic py-3">No visits in this window yet.</div>
          )}
          {perPage.slice(0, 15).map((p) => {
            const pct = (p.views / maxPageViews) * 100;
            return (
              <div
                key={p.path}
                className="relative group"
                data-testid={`pageviews-row-${p.path.replace(/[^a-z0-9]/gi, '-') || 'root'}`}
              >
                <div className="flex items-center justify-between gap-2 px-2 py-1.5 text-xs relative z-10">
                  <span className="text-zinc-200 font-['JetBrains_Mono'] truncate max-w-[60%]">
                    {p.path}
                  </span>
                  <span className="text-zinc-400 text-[11px]">
                    <span className="text-white font-bold">{p.views}</span>
                    <span className="text-zinc-500"> views · </span>
                    <span className="text-[#FF5E1A]">{p.unique_visitors}</span>
                    <span className="text-zinc-500"> unique</span>
                  </span>
                </div>
                <div
                  className="absolute inset-0 bg-[#FF5E1A]/8 rounded-sm"
                  style={{ width: `${pct}%` }}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Trend sparkline */}
      {trend.length > 0 && (
        <div>
          <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.16em] text-zinc-500 mb-2">
            Over time ({trend.length} buckets)
          </div>
          <div className="flex items-end gap-0.5 h-16 bg-[#0A0A0A] border border-[#27272A] rounded-sm p-2">
            {trend.map((t) => {
              const h = Math.max(2, Math.round((t.views / maxBucketViews) * 100));
              return (
                <div
                  key={t.bucket}
                  className="flex-1 bg-[#FF5E1A]/70 hover:bg-[#FF5E1A] transition-colors rounded-sm"
                  style={{ height: `${h}%` }}
                  title={`${t.bucket} · ${t.views} views · ${t.unique_visitors} unique`}
                />
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-4 text-[10px] font-['JetBrains_Mono'] text-zinc-600">
        Bots filtered · identity hashed daily for privacy · live.
      </div>
    </div>
  );
}

function StatTile({ icon, label, value, loading, testId }) {
  return (
    <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-3" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-zinc-500 mb-1">
        <span className="text-[#FF5E1A]">{icon}</span>
        {label}
      </div>
      <div className="text-xl font-black text-white font-['JetBrains_Mono']">
        {loading ? <Loader2 className="w-4 h-4 animate-spin inline text-zinc-500" /> : value.toLocaleString()}
      </div>
    </div>
  );
}
